import { EntryDateField } from '@/@types/entries';
import { OutputDateField } from '@/@types/outputs';
import { Situation } from '@/@types/situation';
import { TStatus } from '@/@types/status';
import { capitalizeWord } from '@/utils/capitalizeWord';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { format, isAfter, isBefore } from 'date-fns';
import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { DateRangePreset, RANGE_PRESETS } from '@/utils/date-range';
import { Modal } from '../Modal';
import { ModalTitle } from '../Modal/common';

type TFilters = {
  startDate: string;
  endDate: string;
  showDeleted: boolean;
  status: TStatus;
  situationId?: string;
  dateField: EntryDateField | OutputDateField;
};

interface IFiltersModalProps {
  isOpen: boolean;
  defaultStartDate: Date;
  defaultEndDate: Date;
  showDeleted: boolean;
  defaultStatus: TStatus;
  defaultDateField: EntryDateField | OutputDateField;
  dateFields: Array<{
    id: EntryDateField | OutputDateField;
    label: string;
  }>;
  defaultSituationId?: string;
  situations?: Situation[];
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onApply: (filters: TFilters) => void;
}

const STATUS = [
  { id: 'todos', label: 'Todos' },
  { id: 'pendente', label: 'Pendente' },
  { id: 'finalizado', label: 'Finalizado' },
];

export function FiltersModal({
  isOpen,
  defaultStartDate,
  defaultEndDate,
  showDeleted,
  defaultStatus,
  defaultDateField,
  dateFields,
  defaultSituationId,
  situations,
  onClose,
  onApply,
}: IFiltersModalProps) {
  const [startDate, setStartDate] = useState<Date | null>(
    defaultStartDate || new Date()
  );
  const [endDate, setEndDate] = useState<Date | null>(
    defaultEndDate || new Date()
  );
  const [listDeleted, setListDeleted] = useState(showDeleted);
  const [status, setStatus] = useState(defaultStatus || 'todos');
  const [selectedSituation, setSelectedSituation] = useState(
    defaultSituationId || 'todos'
  );
  const [dateField, setDateField] = useState(defaultDateField || 'created_at');
  const [rangeMenuAnchorEl, setRangeMenuAnchorEl] =
    useState<null | HTMLElement>(null);

  const hasSituations = !!situations?.length;
  const isRangeMenuOpen = Boolean(rangeMenuAnchorEl);
  const defaultStartTime = defaultStartDate?.getTime();
  const defaultEndTime = defaultEndDate?.getTime();

  const isRangeInvalid =
    !!startDate && !!endDate && isAfter(startDate, endDate);

  const isApplyDisabled = !startDate || !endDate || isRangeInvalid;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setStartDate(defaultStartDate || new Date());
    setEndDate(defaultEndDate || new Date());
    setListDeleted(showDeleted);
    setStatus(defaultStatus || 'todos');
    setSelectedSituation(defaultSituationId || 'todos');
    setDateField(defaultDateField || 'created_at');
    setRangeMenuAnchorEl(null);
  }, [
    isOpen,
    defaultStartTime,
    defaultEndTime,
    showDeleted,
    defaultStatus,
    defaultSituationId,
    defaultDateField,
  ]);

  const handleStartDateChange = (nextDate: Date | null) => {
    setStartDate(nextDate);

    if (nextDate && endDate && isAfter(nextDate, endDate)) {
      setEndDate(nextDate);
    }
  };

  const handleEndDateChange = (nextDate: Date | null) => {
    setEndDate(nextDate);

    if (nextDate && startDate && isBefore(nextDate, startDate)) {
      setStartDate(nextDate);
    }
  };

  const handleOpenRangeMenu = (event: MouseEvent<HTMLElement>) => {
    setRangeMenuAnchorEl(event.currentTarget);
  };

  const handleCloseRangeMenu = () => {
    setRangeMenuAnchorEl(null);
  };

  const handleApplyPreset = (preset: DateRangePreset) => {
    const { startDate: presetStartDate, endDate: presetEndDate } =
      preset.getRange();

    setStartDate(presetStartDate);
    setEndDate(presetEndDate);
    handleCloseRangeMenu();
  };

  const handleApply = () => {
    onApply({
      startDate: format(startDate as Date, 'yyyy-MM-dd'),
      endDate: format(endDate as Date, 'yyyy-MM-dd'),
      showDeleted: listDeleted,
      status,
      situationId: selectedSituation,
      dateField,
    });
    onClose();
  };
  return (
    <Modal open={isOpen} onClose={onClose} fullWidth maxWidth="xs">
      <ModalTitle title="Filtros" onClose={onClose} />
      <DialogContent sx={{ p: 2 }}>
        <Stack spacing={2} pt={1}>
          <Stack
            direction={{
              xs: 'column',
              sm: 'row',
            }}
            spacing={2}
            pt={1}
          >
            <DatePicker
              label="Data Inicial"
              value={startDate}
              onChange={handleStartDateChange}
              sx={{ flex: 1 }}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flex: 1 }}
            >
              <DatePicker
                label="Data Final"
                value={endDate}
                onChange={handleEndDateChange}
                sx={{ flex: 1 }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: isRangeInvalid,
                    helperText: isRangeInvalid
                      ? 'Data final não pode ser menor que a data inicial.'
                      : undefined,
                  },
                }}
              />
              <Tooltip title="Faixas rápidas" disableInteractive>
                <IconButton
                  aria-label="Faixas rápidas"
                  aria-controls={
                    isRangeMenuOpen ? 'date-range-menu' : undefined
                  }
                  aria-haspopup="true"
                  aria-expanded={isRangeMenuOpen ? 'true' : undefined}
                  size="small"
                  onClick={handleOpenRangeMenu}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
              <Menu
                id="date-range-menu"
                anchorEl={rangeMenuAnchorEl}
                open={isRangeMenuOpen}
                onClose={handleCloseRangeMenu}
              >
                {RANGE_PRESETS.map((preset) => (
                  <MenuItem
                    key={preset.id}
                    onClick={() => handleApplyPreset(preset)}
                  >
                    {preset.label}
                  </MenuItem>
                ))}
              </Menu>
            </Stack>
          </Stack>
          <FormControl>
            <InputLabel id="date-field-select-label">Campo Data</InputLabel>
            <Select
              labelId="date-field-select-label"
              id="date-field-select"
              value={dateField}
              label="Campo Data"
              onChange={({ target }) => setDateField(target.value as any)}
            >
              {dateFields?.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel id="status-select-label">Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={hasSituations ? selectedSituation : status}
              label="Status"
              onChange={({ target }) =>
                hasSituations
                  ? setSelectedSituation(target.value)
                  : setStatus(target.value as any)
              }
            >
              {hasSituations
                ? [{ id: 'todos', name: 'Todos' }, ...situations].map(
                    (item) => (
                      <MenuItem key={item.id} value={item.id}>
                        {capitalizeWord(item.name)}
                      </MenuItem>
                    )
                  )
                : STATUS.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.label}
                    </MenuItem>
                  ))}
            </Select>
          </FormControl>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!listDeleted}
                  onChange={() => setListDeleted(!listDeleted)}
                />
              }
              label="Listar Excluídos"
            />
          </FormGroup>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
        <Button onClick={onClose}>Fechar</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={isApplyDisabled}
        >
          Aplicar
        </Button>
      </DialogActions>
    </Modal>
  );
}
